import { useState, useEffect } from 'react';
import { useRecordContext } from 'react-admin';
import {
  List,
  Datagrid,
  DateField,
  ListProps,
  TextField,
  useUpdate,
  useNotify,
  FieldProps,
  TextInput,
  SelectInput,
  useResourceContext
} from 'react-admin';
import AllIcon from '@mui/icons-material/RecordVoiceOver';
import PendingIcon from '@mui/icons-material/VolumeUp';
import { Answer } from '../domain.js';
import { AudioField, LinkToParticipantField } from '../components/fields.js';
import { DefaultPagination } from '../components/pagination.js';

export const AllAnswerIcon = AllIcon;
export const PendingAnswerIcon = PendingIcon;

const postFilters = [
  <SelectInput 
    key="question"
    label="Question" 
    source="question_id"
    parse={(value) => value === '' ? undefined : value}
    choices={[
      { id: 'alright', name: 'alright' },
      { id: 'awake', name: 'awake' },
      { id: 'father', name: 'father' },
      { id: 'holding-hand', name: 'holding-hand' },
      { id: 'not-returning', name: 'not-returning' },
      { id: 'past', name: 'past' },
      { id: 'secret-budapest', name: 'secret-budapest' },
      { id: 'promise', name: 'promise' },
      { id: 'voyeur', name: 'voyeur' },
      { id: 'describe-yourself', name: 'describe-yourself' },
    ]} 
  />,
  <TextInput 
    key="redacted_transcript"
    label="Transcript search"
    source="redacted_transcript@ilike"
    parse={(value) => {
      if (value === '') return undefined;
      return value;
    }}
  />,
  <TextInput 
    key="tags"
    label="Tags search"
    source="tags@ilike"
    parse={(value) => {
      if (value === '') return undefined;
      return value;
    }}
  />,
  <TextInput 
    key="lang"
    label="Language" 
    source="lang" 
    //resettable={true}
    parse={(value) => value === '' ? undefined : value}
  />,
  <SelectInput 
    key="rating"
    label="Rating" 
    source="moderation_rating" 
    // resettable={true}
    parse={(value) => {
      if (value === '') return undefined;
      const num = Number(value);
      return isNaN(num) ? undefined : num;
    }}
    choices={[
      { id: 0, name: '0' },
      { id: 1, name: '1' },
      { id: 2, name: '2' },
      { id: 3, name: '3' },
      { id: 4, name: '4' },
      { id: 5, name: '5' },
    ]}
  />,
  <TextInput 
    key="participant"
    label="Participant ID" 
    source="participant_id" 
    // resettable={true}
    parse={(value) => value === '' ? undefined : value}
  />,
];

export const AnswerList: React.FC<ListProps> = (props) => {
  // console.log('AnswerList props:', props);
  return (
    <List
      {...props}
      filters={postFilters}
      perPage={100}
      pagination={<DefaultPagination />}
      exporter={false}
      sort={{ field: 'created_at', order: 'DESC' }}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Datagrid expand={<AnswerExpandDetails {...({} as any)} />}>
        <TextField source="show_id" />
        <LinkToParticipantField source="participant_id" />
        <TextField source="question_id" />
        <AudioField source="processed_audio" sortable={false} label="Answer" />
        <DateField source="reviewed_at" showTime={true} locales={['en-GB']} />
        <RatingEditField source="moderation_rating"  label="Rating" />
        <DateField source="created_at" showTime={true} locales={['en-GB']} />
      </Datagrid>
    </List>
  );
};

const RatingEditField: React.FC<FieldProps> = ({ source = '' }) => {
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const resource = useResourceContext();

  if (!record) {
    console.log('RatingEditField: record is undefined, not rendering field');
    return null;
  }

  const val = record?.[source] ?? '';

  const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const value = e.target.value === '' ? null : Number(e.target.value);
    console.log('Update answer:', {
      resource,
      params: {
        id: record.id,
        data: { [source]: value },
        previousData: record,
      }
    });
    
    update(
      resource,
      {
        id: record.id,
        data: { [source]: value },
        previousData: record,
      },
      {
        mutationMode: 'optimistic',
        onSuccess: () => {
          notify('Rating updated', { type: 'info' });
        },
        onError: (error) => {
          console.error('Update error details:', error);
          notify('Error updating rating', { type: 'warning' });
        },
      }
    );
  };

  return (
    <select
      value={val != null ? val.toString() : ''}
      onChange={handleChange}
    >
      <option value="">--</option>
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n.toString()}>{n}</option>
      ))}
    </select>

  );
};

const AnnotationBlock: React.FC<{ answer: Answer}> = ({ }) => {
  const record = useRecordContext();
  if (!record) return null;
  const answer = record as Answer;
  const [update] = useUpdate();
  const notify = useNotify();

  const [reviewedTranscript, setReviewedTranscript] = useState(
    answer.reviewed_transcript ?? answer.redacted_transcript ?? ''
  );
  const [currentTags, setCurrentTags] = useState(answer.tags ?? '');
  const [currentObs, setCurrentObs] = useState(answer.observations ?? '');

  const handleTranscriptChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setReviewedTranscript(e.target.value);
  };

  const handleTagsChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCurrentTags(e.target.value);
  };

  const handleObsChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setCurrentObs(e.target.value);
  };
  
  // Set save button label
  var saveLabel = "Update";
  if (answer.reviewed_transcript == null) saveLabel = "Save"; 
 
  // Handle save button
  const handleSave = () => {
    update(
      'answer',
      {
        id: answer.id,
        data: {
          reviewed_transcript: reviewedTranscript,
          tags: currentTags,
          reviewed_at: 'now',
          observations: currentObs,
        },
        previousData: answer,
      },
      {
        onSuccess: () => notify('Annotation updated', { type: 'info' }),
        onError: (error) => {
          console.error('Update error details:', error);
          notify('Error updating annotation', { type: 'warning' });
        },
      }
    );
  };

  // create annotationBlock
  
  if (answer.transcript) {
    return (
      <>
        <textarea rows={reviewedTranscript.split(/\r\n|\r|\n/).length+1} defaultValue={reviewedTranscript} onChange={handleTranscriptChange}/>
        <input style={{backgroundColor:'#ffc',padding:'5px',border:0}} defaultValue={answer.tags} placeholder='Enter comma separated tags for redaction and themes' onChange={handleTagsChange}/>
        <textarea rows={3} defaultValue={answer.observations} placeholder='Enter observations and notes' onChange={handleObsChange} />
        <button disabled={false} onClick={handleSave}>{saveLabel}</button>
      </>
    ) 
  }
  else {
    return (
      <p> No transcript available </p>
    );
  }
};

const AnswerExpandDetails: React.FC<{ record: Answer }> = ({ record }) => {
  const answer = record;
 
  return (
    <div style={{ display: 'flex', gap: '20px' }}>
      <div style={{ flex: 4, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <AnnotationBlock answer={answer} />
      </div>
    </div>
  );
};
